/**
 * Carryable object logic: pickup, drop, return, carry penalty, capture scoring.
 * Pure functions — no side effects, no rendering.
 */

import type { GameState, PlayerState, PlanetState, CarryableObjectState, ModeConfig, Vec3 } from '../types';
import { CARRYABLE_PICKUP_RANGE, DEFAULT_CARRIER_SPEED_PENALTY } from '../constants';
import { vecNormalize, vecDot, vecCross, vecLength, slerp } from '../math';
import { dacos } from './dmath';

// =============================================================================
// QUERY HELPERS
// =============================================================================

/**
 * Get carryable objects on the ground (not carried) on a specific planet.
 */
export function getCarryablesOnPlanet(
  carryables: CarryableObjectState[],
  planetIndex: number
): CarryableObjectState[] {
  return carryables.filter(
    c => c.alive && c.carriedBy === null && c.planetIndex === planetIndex
  );
}

/**
 * Get the carryable object a player is currently carrying, if any.
 */
export function getPlayerCarrying(
  playerId: number,
  carryables: CarryableObjectState[]
): CarryableObjectState | null {
  return carryables.find(c => c.alive && c.carriedBy === playerId) ?? null;
}

/**
 * Check if a carryable object is at its origin (home) position.
 */
export function isAtBase(object: CarryableObjectState): boolean {
  if (object.planetIndex !== object.originPlanetIndex) return false;
  const d = vecDot(
    vecNormalize(object.surfaceDir),
    vecNormalize(object.originSurfaceDir)
  );
  return d > 0.99; // ~8 degree threshold
}

// =============================================================================
// PICKUP
// =============================================================================

/**
 * Arc distance between two surface directions on a planet.
 */
function surfaceArcDistance(dir1: Vec3, dir2: Vec3, radius: number): number {
  const d1 = vecNormalize(dir1);
  const d2 = vecNormalize(dir2);
  const dot = Math.max(-1, Math.min(1, vecDot(d1, d2)));
  return dacos(dot) * radius;
}

/**
 * Check if a player can pick up a specific carryable object.
 */
export function canPickup(
  player: PlayerState,
  object: CarryableObjectState,
  planets: PlanetState[],
  carryables: CarryableObjectState[],
  pickupRange: number = CARRYABLE_PICKUP_RANGE
): boolean {
  if (!player.alive || player.airborne) return false;
  if (!object.alive || object.carriedBy !== null) return false;
  if (player.planetIndex !== object.planetIndex) return false;

  // Can't pick up own team's object (unless it's neutral)
  if (object.teamIndex >= 0 && object.teamIndex === player.teamIndex) return false;

  // Already carrying something
  if (getPlayerCarrying(player.id, carryables) !== null) return false;

  // Range check
  const planet = planets[player.planetIndex];
  const dist = surfaceArcDistance(player.surfaceDir, object.surfaceDir, planet.radius);
  return dist <= pickupRange;
}

/**
 * Get all objects a player can pick up.
 */
export function getPickupableObjects(
  player: PlayerState,
  carryables: CarryableObjectState[],
  planets: PlanetState[]
): CarryableObjectState[] {
  return carryables.filter(c => canPickup(player, c, planets, carryables));
}

/**
 * Pick up a carryable object. Sets carriedBy and deducts movement.
 */
export function pickupObject(
  state: GameState,
  playerId: number,
  objectId: number
): GameState {
  const player = state.players.find(p => p.id === playerId);
  const object = state.carryables.find(c => c.id === objectId);
  if (!player || !object) return state;
  if (!canPickup(player, object, state.planets, state.carryables)) return state;

  const newCarryables = state.carryables.map(c =>
    c.id === objectId ? { ...c, carriedBy: playerId } : c
  );

  return { ...state, carryables: newCarryables };
}

// =============================================================================
// DROP
// =============================================================================

/**
 * Drop a carried object at the player's current position.
 */
export function dropObject(
  state: GameState,
  playerId: number
): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const carrying = getPlayerCarrying(playerId, state.carryables);
  if (!carrying) return state;

  const newCarryables = state.carryables.map(c =>
    c.id === carrying.id
      ? { ...c, carriedBy: null, planetIndex: player.planetIndex, surfaceDir: { ...player.surfaceDir } }
      : c
  );

  return { ...state, carryables: newCarryables };
}

/**
 * Drop a carried object when a player dies (at their last position).
 */
export function dropOnDeath(
  state: GameState,
  playerId: number
): GameState {
  const carrying = getPlayerCarrying(playerId, state.carryables);
  if (!carrying) return state;

  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const newCarryables = state.carryables.map(c =>
    c.id === carrying.id
      ? { ...c, carriedBy: null, planetIndex: player.planetIndex, surfaceDir: { ...player.surfaceDir } }
      : c
  );

  return { ...state, carryables: newCarryables };
}

// =============================================================================
// RETURN
// =============================================================================

/**
 * Check if a player can return (touch-return) a dropped friendly object.
 */
export function canReturnObject(
  player: PlayerState,
  object: CarryableObjectState,
  planets: PlanetState[]
): boolean {
  if (!player.alive || player.airborne) return false;
  if (!object.alive) return false;
  if (object.carriedBy !== null) return false; // must be on the ground
  if (object.teamIndex !== player.teamIndex) return false; // must be friendly
  if (player.planetIndex !== object.planetIndex) return false;
  if (isAtBase(object)) return false; // already at base

  const planet = planets[player.planetIndex];
  const dist = surfaceArcDistance(player.surfaceDir, object.surfaceDir, planet.radius);
  return dist <= CARRYABLE_PICKUP_RANGE;
}

/**
 * Auto-check if a player walked onto a dropped friendly object and return it to origin.
 */
export function checkTouchReturn(
  state: GameState,
  playerId: number
): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const friendlyDropped = state.carryables.filter(
    c => c.alive && c.carriedBy === null && c.teamIndex === player.teamIndex
  );

  let updated = state;
  for (const obj of friendlyDropped) {
    if (canReturnObject(player, obj, state.planets)) {
      updated = {
        ...updated,
        carryables: updated.carryables.map(c =>
          c.id === obj.id
            ? {
                ...c,
                planetIndex: c.originPlanetIndex,
                surfaceDir: { ...c.originSurfaceDir },
              }
            : c
        ),
      };
    }
  }

  return updated;
}

// =============================================================================
// CARRY CONSTRAINTS
// =============================================================================

/**
 * Get movement penalty for a carrier (0.0 if not carrying).
 */
export function getCarrierMovementPenalty(
  playerId: number,
  carryables: CarryableObjectState[],
  modeConfig: ModeConfig
): number {
  const carrying = getPlayerCarrying(playerId, carryables);
  if (!carrying) return 0;
  return modeConfig.carrierSpeedPenalty ?? DEFAULT_CARRIER_SPEED_PENALTY;
}

/**
 * Check if a carrier can jump (false when carrying + config says no).
 */
export function canCarrierJump(
  playerId: number,
  carryables: CarryableObjectState[],
  modeConfig: ModeConfig
): boolean {
  const carrying = getPlayerCarrying(playerId, carryables);
  if (!carrying) return true;
  return modeConfig.carrierCanJump !== false; // default true unless explicitly false
}

// =============================================================================
// CAPTURE (CTF)
// =============================================================================

/**
 * Check if a player is at their home base carrying an enemy object.
 */
export function checkCapture(
  state: GameState,
  playerId: number
): { captured: boolean; objectId: number } | null {
  const player = state.players.find(p => p.id === playerId);
  if (!player || !player.alive || player.airborne) return null;

  const carrying = getPlayerCarrying(playerId, state.carryables);
  if (!carrying) return null;

  // Must be enemy team's object
  if (carrying.teamIndex === player.teamIndex) return null;

  // Must be at own home base
  const homeTeamPlanets = state.config.modeConfig.homeTeamPlanets;
  if (!homeTeamPlanets) return null;

  const homePlanet = homeTeamPlanets[player.teamIndex];
  if (homePlanet === undefined || player.planetIndex !== homePlanet) return null;

  // Check own team's object is at base (optional: some CTF variants require this)
  // For now, capture works regardless of own flag status

  return { captured: true, objectId: carrying.id };
}

/**
 * Execute a capture: increment team score, return object to origin.
 */
export function executeCapture(
  state: GameState,
  playerId: number,
  objectId: number
): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  // Return the captured object to its origin
  const newCarryables = state.carryables.map(c =>
    c.id === objectId
      ? {
          ...c,
          carriedBy: null,
          planetIndex: c.originPlanetIndex,
          surfaceDir: { ...c.originSurfaceDir },
        }
      : c
  );

  // Increment team score in mode state
  let newModeState = state.modeState;
  if (newModeState.ctf) {
    const newScores = { ...newModeState.ctf.scores };
    newScores[player.teamIndex] = (newScores[player.teamIndex] ?? 0) + 1;
    const flagStatus = computeFlagStatus(newCarryables);
    newModeState = { ...newModeState, ctf: { ...newModeState.ctf, scores: newScores, flagStatus } };
  }

  return { ...state, carryables: newCarryables, modeState: newModeState };
}

// =============================================================================
// FLAG STATUS HELPER
// =============================================================================

/**
 * Compute per-team flag/object status from carryable state.
 */
export function computeFlagStatus(
  carryables: CarryableObjectState[]
): Record<number, 'base' | 'carried' | 'dropped'> {
  const status: Record<number, 'base' | 'carried' | 'dropped'> = {};

  for (const c of carryables) {
    if (!c.alive || c.teamIndex < 0) continue;

    if (c.carriedBy !== null) {
      status[c.teamIndex] = 'carried';
    } else if (isAtBase(c)) {
      status[c.teamIndex] = 'base';
    } else {
      status[c.teamIndex] = 'dropped';
    }
  }

  return status;
}

// =============================================================================
// RESPAWN AT BASE (off-map death)
// =============================================================================

/**
 * When a carrier dies off-map (star, space, wormhole), respawn the carried
 * core at the team's crashed drop can. Falls back to origin position.
 */
export function respawnCoreAtBase(
  state: GameState,
  playerId: number
): GameState {
  const carrying = getPlayerCarrying(playerId, state.carryables);
  if (!carrying) return state;

  // Find team's crashed drop can (the core's own team, not the carrier's)
  const can = state.crashedDropCans.find(
    c => c.teamIndex === carrying.teamIndex && c.health > 0
  );

  if (can) {
    // Offset the core ~0.15 radians from the drop can so it's visible beside it
    const canDir = vecNormalize(can.surfaceDir);
    const up: Vec3 = { x: 0, y: 1, z: 0 };
    let tangent = vecCross(canDir, up);
    if (vecLength(tangent) < 0.01) tangent = vecCross(canDir, { x: 1, y: 0, z: 0 });
    tangent = vecNormalize(tangent);
    const offsetDir = vecNormalize(slerp(canDir, tangent, 0.15));

    const newCarryables = state.carryables.map(c =>
      c.id === carrying.id
        ? { ...c, carriedBy: null, planetIndex: can.planetIndex, surfaceDir: { ...offsetDir } }
        : c
    );
    return { ...state, carryables: newCarryables };
  }

  // Fallback: return to origin position
  const newCarryables = state.carryables.map(c =>
    c.id === carrying.id
      ? { ...c, carriedBy: null, planetIndex: c.originPlanetIndex, surfaceDir: { ...c.originSurfaceDir } }
      : c
  );
  return { ...state, carryables: newCarryables };
}
